import {
  PayloadAction,
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { EditorStringModel } from '../Types/EditorString';
import { ResponsiveImageModel } from '../Types/ResponsiveImageModel';
import { post } from '../Services/http';
import Product from '../Types/Product';
import { trackingServiceAddToWishListEvent } from '../Services/Tracking.service';
import { RootState } from '../reducers';

export interface WishListProps {
  id: string;
  title?: string;
  type?: string;
  description?: EditorStringModel;
  items?: WishListItem[];
  shareUri?: string;
  shareModal?: ShareModal;
  shareStatus: number;
}

export enum ShareStatus {
  none,
  pending,
  fulfilled,
  rejected,
}

export interface WishListItem {
  id: string;
  listId: string;
  variantId: string;
  sortIndex: string;
  quantity: number;
  product: Product;
}

interface ShareModal {
  title: string;
  description: string;
}

const DEFAULT_STATE: WishListProps = {
  id: '',
  shareStatus: ShareStatus.none,
};

const mapToAddToWishListItem = (item: Product) => {
  return { articleNumber: item.id, quantity: 1 };
};

const mapToEmailItem = ({
  email,
  message,
  shareUri,
}: {
  email: String;
  message: String;
  shareUri: String;
}) => {
  return { email: email, message: message, shareUri: shareUri };
};

export const addProductToWishList = createAsyncThunk(
  'wishlist/add',
  async (item: Product, { getState }) => {
    const response = await post(
      '/api/wishlist/add',
      mapToAddToWishListItem(item)
    );
    const { cart } = getState() as RootState;
    trackingServiceAddToWishListEvent(item, cart.currency);
    return await response.json();
  }
);

export const update = createAsyncThunk(
  'wishlist/update',
  async (state: WishListProps) => {
    const response = await post('/api/wishlist/update', state);
    return await response.json();
  }
);

export const removeProductFromWishList = createAsyncThunk(
  'wishlist/remove',
  async (item: Product) => {
    const articleNumber = item.id ?? '';
    const response = await post(
      '/api/wishlist/remove',
      mapToAddToWishListItem(item)
    );
    return await response.json();
  }
);

export const shareListAsMail = createAsyncThunk(
  'wishlist/share',
  async ({
    email,
    message,
    shareUri,
  }: {
    email: String;
    message: String;
    shareUri: String;
  }) => {
    const response = await post(
      '/api/wishlist/share',
      mapToEmailItem({
        email: email,
        message: message,
        shareUri: shareUri,
      })
    );
    return await response.json();
  }
);

export const addToCart = createAsyncThunk(
  'wishlist/addToCart',
  async () => {
    const response = await post('/api/wishlist/addtocart');
    return await response.json();
  }
);

const WishListSlice = createSlice({
  name: 'wishList',
  initialState: DEFAULT_STATE,
  reducers: {
    removeWishListItem: (
      state,
      action: PayloadAction<{ product: Product }>
    ) => {
      var articles = state.items;
      const index = articles?.findIndex(
        (x) => x.product.id === action.payload.product.id
      );
      if (index > -1) {
        state.items?.splice(index, 1);
      }
    },
    resetshareStatus: (state, action) => {
      state.shareStatus = ShareStatus.none;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(shareListAsMail.fulfilled, (state, action) => {
      state.shareStatus = ShareStatus.fulfilled;
    });
    builder.addCase(shareListAsMail.pending, (state, action) => {
      state.shareStatus = ShareStatus.pending;
    });
    builder.addCase(shareListAsMail.rejected, (state, action) => {
      state.shareStatus = ShareStatus.rejected;
    });
  },
});

export const WishListActions = WishListSlice.actions;

export default WishListSlice.reducer;
